// Upload manager — HANDOFF schema Section 13.
// Presigned-URL direct-to-R2 upload with exponential backoff retry.
// Called from SessionCompleteScreen (immediate attempt) and DashboardScreen
// (retry sweep on every dashboard appear, per spec).

import { signUpload } from '../worker/api';
import { updatePendingUpload, getUploadsByStatus, updateSessionHistory } from '../db/indexedDB';

const MAX_ATTEMPTS = 10;
const MAX_BACKOFF_MINUTES = 60;

function backoffMinutes(attemptCount) {
  return Math.min(2 ** attemptCount, MAX_BACKOFF_MINUTES);
}

async function putToR2(url, blob) {
  // Per R2 presigned-URL requirements: do NOT set Content-Type or any other
  // header that wasn't included in the signature — the Worker signs with
  // signQuery only, so the browser must send a bare PUT with just the body.
  const res = await fetch(url, { method: 'PUT', body: blob });
  if (!res.ok) throw new Error(`Upload failed (${res.status}) for ${url}`);
}

/** Attempts to upload a single pending_uploads record. Mutates its status in IndexedDB. */
export async function attemptUpload(record) {
  try {
    const { presigned_urls } = await signUpload({
      participantId: record.participant_id,
      sessionId: record.session_id,
      files: ['manifest.json', 'text_audio.webm', 'image_audio.webm'].filter(
        (f) => f !== 'image_audio.webm' || record.image_audio_blob
      ),
    });

    await putToR2(presigned_urls['manifest.json'], new Blob([JSON.stringify(record.manifest)], { type: 'application/json' }));
    await putToR2(presigned_urls['text_audio.webm'], record.text_audio_blob);
    if (record.image_audio_blob) {
      await putToR2(presigned_urls['image_audio.webm'], record.image_audio_blob);
    }

    await updatePendingUpload(record.session_id, {
      status: 'complete',
      last_attempt_at: new Date().toISOString(),
    });
    await updateSessionHistory(record.session_id, { upload_status: 'complete' });
    return { ok: true };
  } catch (err) {
    const attempt_count = (record.attempt_count ?? 0) + 1;
    const status = attempt_count >= MAX_ATTEMPTS ? 'permanently_failed' : 'failed';
    await updatePendingUpload(record.session_id, {
      status,
      attempt_count,
      last_attempt_at: new Date().toISOString(),
    });
    await updateSessionHistory(record.session_id, { upload_status: status === 'complete' ? 'complete' : 'failed' });

    if ('serviceWorker' in navigator && 'SyncManager' in window) {
      try {
        const reg = await navigator.serviceWorker.ready;
        await reg.sync.register('aspire-upload-retry');
      } catch {
        /* BackgroundSync unsupported (e.g. Safari) — dashboard-appear retry covers it */
      }
    }
    return { ok: false, error: err };
  }
}

/** Retry sweep — call on dashboard mount and on BackgroundSync events. */
export async function retryPendingUploads() {
  const candidates = await getUploadsByStatus(['pending', 'failed']);
  const now = Date.now();
  const results = [];

  for (const record of candidates) {
    const attempt_count = record.attempt_count ?? 0;
    if (record.last_attempt_at) {
      const dueAt = new Date(record.last_attempt_at).getTime() + backoffMinutes(attempt_count) * 60_000;
      if (now < dueAt) continue; // not due yet
    }
    results.push(await attemptUpload(record));
  }
  return results;
}
